import { featureFlagOverrides } from '@/constants/feature-flag-overrides.ts';
import { ThemeProvider } from '@material-ui/core/styles';
import { ConfigCatProvider, DataGovernance, PollingMode } from 'configcat-react';
import { useEffect, useRef } from 'react';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import DevPrimarySecondarySelector from '@/components/DevPrimarySecondarySelector.jsx';
import DevVersionLabel from '@/components/DevVersionLabel.jsx';
import HtmlHead from '@/components/HtmlHead.jsx';
import Router from '@/components/Router.jsx';

import useAppInit from '@/hooks/useAppInit.js';

import { dayjs } from '@/utils/dayjsSetup.js';
import { isDevEnvironment } from '@/utils/helpers.js';

import { useConfigStore, usePrimarySecondaryOverrideStore, usePrismicStore } from '@/store.js';

import AppProviders from './AppProviders.tsx';
import { createTheme } from './theme/index.js';

const configCatSdkKey = import.meta.env.VITE_CONFIG_CAT_SDK_KEY;

if (!configCatSdkKey) {
    console.warn(
        'VITE_CONFIG_CAT_SDK_KEY is missing — rendering without ConfigCatProvider; feature flags will fall back to their default values.'
    );
}

export default function App() {
    useAppInit();
    const prismicStartTime = useRef<number>(performance.now());
    const prismicStartLang = useRef<string | null>(null);
    const { i18n } = useTranslation();
    const currentCustomer = useConfigStore((state: any) => state.currentCustomer);
    const previewPrimary = usePrimarySecondaryOverrideStore((state: any) =>
        isDevEnvironment() ? state.primaryColor : null
    );
    const previewSecondary = usePrimarySecondaryOverrideStore((state: any) =>
        isDevEnvironment() ? state.secondaryColor : null
    );

    // Measure time until customers data is ready (spinner disappears)
    const customers = usePrismicStore((state: any) => state.customers);
    useEffect(() => {
        if (prismicStartLang.current !== i18n.language) {
            prismicStartTime.current = performance.now();
            prismicStartLang.current = i18n.language;
        }
    }, [i18n.language]);

    useEffect(() => {
        if (!customers || prismicStartLang.current !== i18n.language) return;
        const ms = performance.now() - prismicStartTime.current;
        if (ms < 10) return; // skip if already loaded
        const formatted = ms >= 1000 ? `${(ms / 1000).toFixed(3)}s` : `${ms.toFixed(3)}ms`;
        console.warn(`[Prismic] Time to customers ready (${i18n.language}): ${formatted}`);
    }, [customers, i18n.language]);

    useEffect(() => {
        dayjs.locale(i18n.language);
        document.documentElement.lang = i18n.language;
    }, [i18n.language]);

    const appTree = (
        <ThemeProvider
            theme={createTheme(
                previewPrimary ?? currentCustomer?.primary_color,
                previewSecondary ?? currentCustomer?.secondary_color
            )}
        >
            <AppProviders>
                <HtmlHead />
                {/* <Elements stripe={stripePromise}> */}
                <Router />
                {/* </Elements> */}
                <DevVersionLabel />
                <DevPrimarySecondarySelector />
                <Toaster />
            </AppProviders>
        </ThemeProvider>
    );

    if (!configCatSdkKey) {
        return appTree;
    }

    return (
        <ConfigCatProvider
            sdkKey={configCatSdkKey}
            pollingMode={PollingMode.AutoPoll}
            options={{ flagOverrides: featureFlagOverrides, dataGovernance: DataGovernance.EuOnly }}
        >
            {appTree}
        </ConfigCatProvider>
    );
}
